import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { billingDocService } from '../services/billingDocService';
import { practiceExtrasService } from '../services/practiceExtrasService';
import { patientService } from '../services/patientService';

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const CLAIM_TABS = [
  { id: 'awaiting_submission', label: 'Awaiting Submission' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'rejected', label: 'Rejected/Denied' },
  { id: 'resubmitted', label: 'Resubmitted' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'era_received', label: 'ERA Received' },
  { id: 'paid', label: 'Paid/Processed' },
];

const normalizeClaim = (s) => {
  const map = {
    draft: 'awaiting_submission',
    ready: 'awaiting_submission',
    submitted_manual: 'submitted',
    denied: 'rejected',
    appealed: 'resubmitted',
  };
  return map[s] || s || 'awaiting_submission';
};

const Billing = () => {
  const [tab, setTab] = useState('overview');
  const [claimTab, setClaimTab] = useState('awaiting_submission');
  const [patients, setPatients] = useState([]);
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [claims, setClaims] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [ledgerPatientId, setLedgerPatientId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [invForm, setInvForm] = useState({
    patientId: '',
    amountCents: '',
    description: '',
    cptCodes: '',
  });
  const [claimForm, setClaimForm] = useState({
    patientId: '',
    dateOfService: '',
    payerName: '',
    cptCodes: '',
    diagnosisCodes: '',
    amountCents: '',
  });
  const [insurerForm, setInsurerForm] = useState({ name: '', payerId: '', phone: '' });
  const [payForm, setPayForm] = useState({ invoiceId: '', amount: '', method: 'cash' });
  const [batchAmount, setBatchAmount] = useState('150');

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || 'Patient';

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [p, ov, inv, cl, ins, d] = await Promise.all([
        patientService.getPatients({}),
        practiceExtrasService.billingOverview(),
        practiceExtrasService.listInvoices(),
        practiceExtrasService.listClaims(),
        practiceExtrasService.listInsurers(),
        billingDocService.list(),
      ]);
      setPatients(Array.isArray(p) ? p : p.data || []);
      setOverview(ov.data || null);
      setInvoices(inv.data || []);
      setClaims(cl.data || []);
      setInsurers(ins.data || []);
      setDocs(d.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadLedger = async (patientId) => {
    if (!patientId) {
      setLedger(null);
      return;
    }
    try {
      const res = await practiceExtrasService.getLedger(patientId);
      setLedger(res.data);
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const filteredClaims = claims.filter(
    (c) => normalizeClaim(c.status) === claimTab,
  );

  const printCms1500 = async (id) => {
    try {
      const res = await practiceExtrasService.getClaimCms1500(id);
      const d = res.data || {};
      const w = window.open('', '_blank', 'width=800,height=900');
      if (!w) return;
      w.document.write(`<!DOCTYPE html><html><head><title>CMS-1500 Draft</title>
        <style>body{font-family:Georgia,serif;padding:24px;color:#111}
        h1{font-size:18px} table{width:100%;border-collapse:collapse;margin-top:12px}
        td,th{border:1px solid #333;padding:6px;text-align:left;font-size:13px}
        .note{color:#666;font-size:12px;margin-top:16px}</style></head><body>
        <h1>CMS-1500 Draft (manual print — not electronic submission)</h1>
        <table>
          <tr><th>Patient</th><td>${d.patientName || ''}</td></tr>
          <tr><th>DOB</th><td>${d.patientDob || ''}</td></tr>
          <tr><th>DOS</th><td>${d.dateOfService ? new Date(d.dateOfService).toLocaleDateString() : ''}</td></tr>
          <tr><th>CPT</th><td>${d.cptCodes || ''}</td></tr>
          <tr><th>Diagnosis</th><td>${d.diagnosisCodes || ''}</td></tr>
          <tr><th>Payer</th><td>${d.payerName || ''} (${d.memberId || ''})</td></tr>
          <tr><th>POS</th><td>${d.placeOfService || '11'}</td></tr>
          <tr><th>Amount</th><td>${money(d.amountCents)}</td></tr>
          <tr><th>Billing provider</th><td>${d.billingProviderName || ''}</td></tr>
          <tr><th>Rendering</th><td>${d.renderingProviderName || ''}</td></tr>
          <tr><th>Status</th><td>${d.status || ''}</td></tr>
        </table>
        <p class="note">${d.note || ''}</p>
        <script>window.print()</script></body></html>`);
      w.document.close();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'claims', label: 'Claims' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'ledger', label: 'Client ledger' },
    { id: 'batch', label: 'Batch invoicing' },
    { id: 'insurers', label: 'Insurers' },
    { id: 'docs', label: 'Session docs' },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-primary">Billing</h1>
            <p className="text-gray-500 text-sm mt-1">
              TheraNest-style claims, invoices, client ledger, batch invoicing, and insurers.
              Clearinghouse auto-submit and ERA import remain deferred (manual status tracking + CMS-1500 print).
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  tab === t.id ? 'bg-primary text-white' : 'bg-gray-100'
                }`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600 mb-4">{error}</p>}

          {!loading && tab === 'overview' && overview && (
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-gray-500">Open balance</div>
                <div className="text-2xl font-semibold">{money(overview.openBalanceCents)}</div>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-gray-500">Billed</div>
                <div className="text-2xl font-semibold">{money(overview.billedCents)}</div>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-gray-500">Collected</div>
                <div className="text-2xl font-semibold">{money(overview.paidCents)}</div>
              </div>
              <div className="bg-white rounded-xl shadow p-4">
                <div className="text-xs text-gray-500">Invoices</div>
                <div className="text-2xl font-semibold">{overview.invoiceCount}</div>
              </div>
              <div className="bg-white rounded-xl shadow p-4 md:col-span-2">
                <div className="text-sm font-medium mb-2">Aging (open balances)</div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div>0–30d<br /><strong>{money(overview.aging?.current)}</strong></div>
                  <div>31–60d<br /><strong>{money(overview.aging?.days30)}</strong></div>
                  <div>61–90d<br /><strong>{money(overview.aging?.days60)}</strong></div>
                  <div>90d+<br /><strong>{money(overview.aging?.days90)}</strong></div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow p-4 md:col-span-2">
                <div className="text-sm font-medium mb-2">Claims by status</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {CLAIM_TABS.map((t) => (
                    <span key={t.id} className="bg-gray-100 px-2 py-1 rounded">
                      {t.label}: {overview.claimCounts?.[t.id] || 0}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!loading && tab === 'claims' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-1 border-b pb-2">
                {CLAIM_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`px-2 py-1 text-xs rounded ${
                      claimTab === t.id ? 'bg-primary text-white' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setClaimTab(t.id)}
                  >
                    {t.label} (
                    {claims.filter((c) => normalizeClaim(c.status) === t.id).length})
                  </button>
                ))}
              </div>

              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-3 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createClaim({
                    ...claimForm,
                    amountCents: Math.round(Number(claimForm.amountCents || 0) * 100),
                    status: 'awaiting_submission',
                  });
                  setClaimForm({
                    patientId: '',
                    dateOfService: '',
                    payerName: '',
                    cptCodes: '',
                    diagnosisCodes: '',
                    amountCents: '',
                  });
                  await load();
                }}
              >
                <select
                  className="border rounded px-2 py-2 text-sm"
                  value={claimForm.patientId}
                  onChange={(e) => setClaimForm({ ...claimForm, patientId: e.target.value })}
                  required
                >
                  <option value="">Client</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <input
                  type="date"
                  className="border rounded px-2 py-2 text-sm"
                  value={claimForm.dateOfService}
                  onChange={(e) => setClaimForm({ ...claimForm, dateOfService: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Payer"
                  value={claimForm.payerName}
                  onChange={(e) => setClaimForm({ ...claimForm, payerName: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="CPT"
                  value={claimForm.cptCodes}
                  onChange={(e) => setClaimForm({ ...claimForm, cptCodes: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Dx"
                  value={claimForm.diagnosisCodes}
                  onChange={(e) => setClaimForm({ ...claimForm, diagnosisCodes: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Amount USD"
                  value={claimForm.amountCents}
                  onChange={(e) => setClaimForm({ ...claimForm, amountCents: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2 text-sm md:col-span-3">
                  Add claim (Awaiting Submission)
                </button>
              </form>

              <div className="bg-blue-50 text-sm p-3 rounded-lg">
                Total in tab: {filteredClaims.length} · Balance due:{' '}
                {money(filteredClaims.reduce((s, c) => s + (c.amountCents || 0), 0))}
                <span className="text-gray-500 ml-2">
                  (Manual tracking — Submit means you filed outside Peleescribe)
                </span>
              </div>

              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Client</th>
                    <th className="p-3">DOS</th>
                    <th className="p-3">Payer</th>
                    <th className="p-3">CPT</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-gray-500">No claims in this tab.</td>
                    </tr>
                  )}
                  {filteredClaims.map((c) => (
                    <tr key={c._id} className="border-t">
                      <td className="p-3">{patientLabel(c.patientId)}</td>
                      <td className="p-3">
                        {c.dateOfService ? new Date(c.dateOfService).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3">{c.payerName || '—'}</td>
                      <td className="p-3">{c.cptCodes || '—'}</td>
                      <td className="p-3">{money(c.amountCents)}</td>
                      <td className="p-3 space-x-2">
                        <select
                          className="border rounded text-xs px-1 py-1"
                          value={normalizeClaim(c.status)}
                          onChange={async (e) => {
                            await practiceExtrasService.updateClaim(c._id, {
                              status: e.target.value,
                            });
                            await load();
                          }}
                        >
                          {CLAIM_TABS.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="text-primary text-xs underline"
                          onClick={() => printCms1500(c._id)}
                        >
                          CMS-1500
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'invoices' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-4 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createInvoice({
                    ...invForm,
                    amountCents: Math.round(Number(invForm.amountCents) * 100),
                    status: 'sent',
                  });
                  setInvForm({ patientId: '', amountCents: '', description: '', cptCodes: '' });
                  await load();
                }}
              >
                <select
                  className="border rounded px-2 py-2 text-sm"
                  value={invForm.patientId}
                  onChange={(e) => setInvForm({ ...invForm, patientId: e.target.value })}
                  required
                >
                  <option value="">Client</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Amount USD"
                  value={invForm.amountCents}
                  onChange={(e) => setInvForm({ ...invForm, amountCents: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Description"
                  value={invForm.description}
                  onChange={(e) => setInvForm({ ...invForm, description: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2 text-sm">
                  New invoice
                </button>
              </form>

              <form
                className="bg-gray-50 rounded-xl p-4 grid md:grid-cols-4 gap-3 text-sm"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!payForm.invoiceId) return;
                  await practiceExtrasService.payInvoice(payForm.invoiceId, {
                    amountCents: Math.round(Number(payForm.amount) * 100),
                    method: payForm.method,
                  });
                  setPayForm({ invoiceId: '', amount: '', method: 'cash' });
                  await load();
                  if (ledgerPatientId) await loadLedger(ledgerPatientId);
                }}
              >
                <select
                  className="border rounded px-2 py-2"
                  value={payForm.invoiceId}
                  onChange={(e) => setPayForm({ ...payForm, invoiceId: e.target.value })}
                  required
                >
                  <option value="">Accept payment on…</option>
                  {invoices
                    .filter((i) => (i.balanceDueCents ?? i.amountCents - (i.amountPaidCents || 0)) > 0)
                    .map((i) => (
                      <option key={i._id} value={i._id}>
                        {i.invoiceNumber} · {patientLabel(i.patientId)} ·{' '}
                        {money(i.balanceDueCents ?? i.amountCents - (i.amountPaidCents || 0))}
                      </option>
                    ))}
                </select>
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Payment USD"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  required
                />
                <select
                  className="border rounded px-2 py-2"
                  value={payForm.method}
                  onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="check">Check</option>
                  <option value="eft">EFT</option>
                  <option value="insurance">Insurance</option>
                </select>
                <button className="bg-primary text-white rounded-lg px-3 py-2">Accept payment</button>
              </form>

              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Total</th>
                    <th className="p-3">Balance due</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Claim</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const bal =
                      inv.balanceDueCents ??
                      Math.max(0, (inv.amountCents || 0) - (inv.amountPaidCents || 0));
                    return (
                      <tr key={inv._id} className="border-t">
                        <td className="p-3">{inv.invoiceNumber}</td>
                        <td className="p-3">{patientLabel(inv.patientId)}</td>
                        <td className="p-3">{money(inv.amountCents)}</td>
                        <td className="p-3">{money(bal)}</td>
                        <td className="p-3">{inv.status}</td>
                        <td className="p-3">{inv.claimProgress || 'open'}</td>
                        <td className="p-3 space-x-2">
                          {bal > 0 && (
                            <>
                              <button
                                type="button"
                                className="text-primary text-xs underline"
                                onClick={async () => {
                                  try {
                                    const res = await practiceExtrasService.createPayLink(inv._id);
                                    const url = res.data?.url;
                                    if (url) window.prompt('Client pay link (Stripe Checkout):', url);
                                    else alert('No URL returned — check Stripe config');
                                  } catch (e) {
                                    alert(e.response?.data?.message || e.message);
                                  }
                                }}
                              >
                                Pay link
                              </button>
                              <button
                                type="button"
                                className="text-primary text-xs underline"
                                onClick={async () => {
                                  await practiceExtrasService.writeOffInvoice(inv._id, {});
                                  await load();
                                }}
                              >
                                Write-off
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="text-gray-600 text-xs underline"
                            onClick={() => {
                              const w = window.open('', '_blank');
                              if (!w) return;
                              w.document.write(`<!doctype html><html><head><title>${inv.invoiceNumber}</title>
                                <style>body{font-family:Georgia,serif;padding:32px;max-width:640px;margin:auto}
                                h1{font-size:18px}</style></head><body>
                                <h1>Invoice ${inv.invoiceNumber}</h1>
                                <p>${patientLabel(inv.patientId)}</p>
                                <p>${inv.description || ''}</p>
                                <p>Total: ${money(inv.amountCents)} · Balance: ${money(bal)} · Status: ${inv.status}</p>
                                <script>window.print()</script></body></html>`);
                              w.document.close();
                            }}
                          >
                            Print
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'ledger' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={ledgerPatientId}
                  onChange={async (e) => {
                    setLedgerPatientId(e.target.value);
                    await loadLedger(e.target.value);
                  }}
                >
                  <option value="">Select client for ledger…</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                {ledgerPatientId && (
                  <button
                    type="button"
                    className="text-sm text-primary underline"
                    onClick={async () => {
                      try {
                        const res = await practiceExtrasService.getStatement(ledgerPatientId);
                        const st = res.data || {};
                        const rows = (st.invoices || [])
                          .map(
                            (inv) =>
                              `<tr><td>${inv.invoiceNumber}</td><td>${inv.status}</td><td>${money(inv.amountCents)}</td></tr>`,
                          )
                          .join('');
                        const w = window.open('', '_blank');
                        if (!w) return;
                        w.document.write(`<!doctype html><html><head><title>Statement</title>
                          <style>body{font-family:Georgia,serif;padding:32px} table{width:100%;border-collapse:collapse}
                          th,td{border:1px solid #ccc;padding:6px;text-align:left}</style></head><body>
                          <h1>Statement — ${st.patientName || ''}</h1>
                          <p>Balance ${money(st.balanceCents)}</p>
                          <table><thead><tr><th>#</th><th>Status</th><th>Total</th></tr></thead>
                          <tbody>${rows}</tbody></table>
                          <script>window.print()</script></body></html>`);
                        w.document.close();
                      } catch (e) {
                        alert(e.response?.data?.message || e.message);
                      }
                    }}
                  >
                    Print statement
                  </button>
                )}
              </div>

              {ledger && (
                <>
                  <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-6">
                    <div>
                      <div className="text-xs text-gray-500">Account balance</div>
                      <div className="text-2xl font-semibold">{money(ledger.balanceCents)}</div>
                    </div>
                    <div className="text-sm text-gray-500 self-end">
                      Open invoices:{' '}
                      {(ledger.invoices || []).filter(
                        (i) =>
                          (i.balanceDueCents ??
                            i.amountCents - (i.amountPaidCents || 0)) > 0,
                      ).length}
                    </div>
                  </div>

                  <h3 className="font-medium">Open / all invoices</h3>
                  <table className="w-full bg-white rounded-xl shadow text-sm mb-4">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="p-3">Date</th>
                        <th className="p-3">#</th>
                        <th className="p-3">Total</th>
                        <th className="p-3">Balance due</th>
                        <th className="p-3">Claim progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger.invoices || []).map((inv) => (
                        <tr key={inv._id} className="border-t">
                          <td className="p-3">
                            {inv.issueDate
                              ? new Date(inv.issueDate).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="p-3">{inv.invoiceNumber}</td>
                          <td className="p-3">{money(inv.amountCents)}</td>
                          <td className="p-3">
                            {money(
                              inv.balanceDueCents ??
                                inv.amountCents - (inv.amountPaidCents || 0),
                            )}
                          </td>
                          <td className="p-3">
                            {inv.claimProgress === 'in_progress'
                              ? 'In Progress'
                              : inv.claimProgress || 'open'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <h3 className="font-medium">Payment / ledger history</h3>
                  <table className="w-full bg-white rounded-xl shadow text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="p-3">Date</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Description</th>
                        <th className="p-3">Amount</th>
                        <th className="p-3">Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger.entries || []).map((e) => (
                        <tr key={e._id} className="border-t">
                          <td className="p-3">
                            {e.entryDate
                              ? new Date(e.entryDate).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="p-3">{e.entryType}</td>
                          <td className="p-3">{e.description}</td>
                          <td className="p-3">{money(e.amountCents)}</td>
                          <td className="p-3">{e.method || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {!loading && tab === 'batch' && (
            <div className="bg-white rounded-xl shadow p-6 space-y-4 max-w-lg">
              <h2 className="font-medium">Batch invoicing</h2>
              <p className="text-sm text-gray-500">
                Creates invoices for completed appointments (last 90 days) that do not already have
                an invoice — same idea as TheraNest batch invoicing.
              </p>
              <label className="flex flex-col gap-1 text-sm">
                Default session amount (USD)
                <input
                  className="border rounded px-3 py-2"
                  value={batchAmount}
                  onChange={(e) => setBatchAmount(e.target.value)}
                />
              </label>
              <button
                type="button"
                disabled={busy}
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm"
                onClick={async () => {
                  try {
                    setBusy(true);
                    const res = await practiceExtrasService.batchInvoicesFromAppointments({
                      defaultAmountCents: Math.round(Number(batchAmount) * 100),
                    });
                    alert(`Created ${res.data?.created || 0} invoice(s).`);
                    await load();
                  } catch (e) {
                    alert(e.response?.data?.message || e.message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Run batch invoicing
              </button>
            </div>
          )}

          {!loading && tab === 'insurers' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-4 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createInsurer(insurerForm);
                  setInsurerForm({ name: '', payerId: '', phone: '' });
                  await load();
                }}
              >
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Insurer name"
                  value={insurerForm.name}
                  onChange={(e) => setInsurerForm({ ...insurerForm, name: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Payer ID"
                  value={insurerForm.payerId}
                  onChange={(e) => setInsurerForm({ ...insurerForm, payerId: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2 text-sm"
                  placeholder="Phone"
                  value={insurerForm.phone}
                  onChange={(e) => setInsurerForm({ ...insurerForm, phone: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2 text-sm">Add insurer</button>
              </form>
              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Name</th>
                    <th className="p-3">Payer ID</th>
                    <th className="p-3">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {insurers.map((i) => (
                    <tr key={i._id} className="border-t">
                      <td className="p-3">{i.name}</td>
                      <td className="p-3">{i.payerId || '—'}</td>
                      <td className="p-3">{i.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'docs' && (
            <div className="bg-white rounded-xl shadow overflow-x-auto">
              <p className="p-3 text-sm text-gray-500 border-b">
                Clinical billing documentation drafts (approve before export). Separate from
                invoices/claims.
              </p>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">DOS</th>
                    <th className="p-3">Patient</th>
                    <th className="p-3">CPT</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d._id} className="border-t">
                      <td className="p-3">
                        {d.dateOfService
                          ? new Date(d.dateOfService).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-3">
                        {patientLabel(typeof d.patientId === 'object' ? d.patientId : null)}
                      </td>
                      <td className="p-3">{d.suggestedCptCode || '—'}</td>
                      <td className="p-3">{d.status}</td>
                      <td className="p-3">
                        {!d.clinicianApproved && (
                          <button
                            type="button"
                            className="text-primary text-xs underline"
                            onClick={async () => {
                              await billingDocService.approve(d._id);
                              await load();
                            }}
                          >
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Billing;
